import { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { useNavigate, useLocation, type Location } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { LanguageSelector } from '@/components/LanguageSelector';

const AuthBackground = memo(() => (
  <>
    <video autoPlay loop muted playsInline className="fixed inset-0 w-full h-full object-cover">
      <source src="/videos/auth-background.mp4" type="video/mp4" />
    </video>
    <div className="fixed inset-0 bg-black/40" />
  </>
));
AuthBackground.displayName = 'AuthBackground';

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTenantDiscovery, setShowTenantDiscovery] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation(['auth', 'validation', 'notifications']);

  const { user, loading: authLoading, tenantDiscoveryOptions, refreshTenantDiscovery, validationError, databaseValidated } = useAuth();
  const [isNavigating, setIsNavigating] = useState(false);

  const { isPlatformAdmin, loading: platformLoading } = usePlatformAdmin();

  // Use ref to track navigation state without causing re-renders
  const isNavigatingRef = useRef(false);

  // ProtectedRoute sends an involuntary sign-out here with the route the
  // user was on and why. Reading it lets this screen say what happened and
  // return the user to that exact place instead of always /dashboard
  // (T-09, T-10).
  const navigationState = location.state as { from?: Location; reason?: 'expired' | 'network' | 'permission' } | null;
  const returnTo = navigationState?.from?.pathname && navigationState.from.pathname !== '/auth'
    ? `${navigationState.from.pathname}${navigationState.from.search ?? ''}`
    : null;

  // Memoize redirect path to prevent unnecessary recalculations
  const redirectPath = useMemo(() => {
    return returnTo ?? (isPlatformAdmin ? '/secure-admin-portal' : '/dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformAdmin, returnTo]);

  // Stable redirect handler - no dependency on isNavigating state
  const handleRedirect = useCallback(() => {
    if (!isNavigatingRef.current) {
      isNavigatingRef.current = true;
      setIsNavigating(true);
      // Add a small delay to prevent race conditions
      setTimeout(() => {
        navigate(redirectPath);
      }, 100);
    }
  }, [navigate, redirectPath]);

  // Redirect authenticated + validated users away from auth page
  useEffect(() => {
    if (!authLoading && !platformLoading && user && !isNavigatingRef.current
        && databaseValidated && !validationError) {
      handleRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, platformLoading, user, databaseValidated, validationError]);
  // handleRedirect excluded: useCallback + isNavigatingRef guard prevents double-call

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Signup form state
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    businessName: '',
    phoneNumber: ''
  });

  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);

  const handleTenantDiscovery = async () => {
    if (!loginData.email) {
      setError(t('validation:emailRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await refreshTenantDiscovery(loginData.email);
      setShowTenantDiscovery(true);
    } catch (err) {
      setError(t('auth:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      });

      if (error) {
        setError(error.message);
      } else {
        toast({
          title: t('auth:welcomeBack'),
          description: t('auth:loggedInSuccess'),
        });
      }
    } catch (err) {
      setError(t('auth:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validation
    if (signupData.password !== signupData.confirmPassword) {
      setError(t('validation:passwordMatch'));
      setIsLoading(false);
      return;
    }

    if (signupData.password.length < 6) {
      setError(t('validation:passwordMin'));
      setIsLoading(false);
      return;
    }

    // Validate business info (required for all signups)
    if (!signupData.businessName || !signupData.phoneNumber) {
      setError(t('validation:businessNameAndPhone'));
      setIsLoading(false);
      return;
    }

    try {
      const redirectUrl = `${window.location.origin}/dashboard`;

      const { data, error } = await supabase.auth.signUp({
        email: signupData.email,
        password: signupData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: signupData.fullName,
            role: 'tenant_admin',
            business_name: signupData.businessName,
            phone_number: signupData.phoneNumber
          }
        }
      });

      if (error) {
        setError(error.message);
      } else if (data.user) {
        // Email confirmation required — Supabase Send Email Hook handles sending automatically
        setShowConfirmationMessage(true);
      }
    } catch (err) {
      setError(t('auth:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setError(t('validation:emailRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        toast({
          title: t('auth:checkEmail'),
          description: t('auth:resetPasswordDescription'),
        });
        setShowForgotPassword(false);
        setResetEmail('');
      }
    } catch (err) {
      setError(t('auth:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <AuthBackground />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
      {showConfirmationMessage ? (
        <Card className="w-full max-w-md backdrop-blur-xl bg-white/70 border-gray-200/50 dark:bg-white/10 dark:border-white/20 shadow-2xl">
          <CardHeader className="text-center">
            <Mail className="h-12 w-12 text-primary mx-auto mb-2" />
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth:checkEmail')}</CardTitle>
            <CardDescription className="text-gray-700 dark:text-white/80">
              {t('auth:signupConfirmationSent')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowConfirmationMessage(false)}
            >
              {t('auth:goToLogin')}
            </Button>
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-md backdrop-blur-xl bg-white/70 border-gray-200/50 dark:bg-white/10 dark:border-white/20 shadow-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">{t('auth:appTitle')}</CardTitle>
          <CardDescription className="text-gray-700 dark:text-white/80">
            {t('auth:appDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-gray-900 dark:text-white">
          {navigationState?.reason === 'expired' && (
            <Alert className="mb-4 border-amber-500/50 bg-amber-50 dark:bg-amber-900/20">
              <AlertDescription className="text-amber-800 dark:text-amber-300">
                {returnTo ? t('auth:sessionExpiredReturnTo', { path: returnTo }) : t('auth:sessionExpired')}
              </AlertDescription>
            </Alert>
          )}
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">{t('auth:signInTab')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth:signUpTab')}</TabsTrigger>
            </TabsList>

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <TabsContent value="login" className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">{t('auth:email')}</Label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('auth:emailPlaceholder')}
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <Label htmlFor="login-password">{t('auth:password')}</Label>
                    <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
                      <DialogTrigger asChild>
                        <Button
                          variant="link"
                          className="self-start px-0 py-1 h-auto text-xs text-primary underline sm:self-auto"
                        >
                          {t('auth:forgotPassword')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('auth:resetPasswordTitle')}</DialogTitle>
                          <DialogDescription>
                            {t('auth:resetPasswordDescription')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div className="space-y-2">
                            <Label htmlFor="reset-email">{t('auth:email')}</Label>
                            <Input
                              id="reset-email"
                              type="email"
                              placeholder={t('auth:emailPlaceholder')}
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                            />
                          </div>
                          <Button
                            onClick={handleForgotPassword}
                            className="w-full"
                            disabled={isLoading || !resetEmail}
                          >
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t('auth:sendResetLink')}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showLoginPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={t('auth:passwordPlaceholder')}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      aria-label={showLoginPassword ? t('auth:hidePassword') : t('auth:showPassword')}
                    >
                      {showLoginPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                 {/* Tenant Discovery Results - Hidden for now, can be re-enabled later */}
                 {/* {showTenantDiscovery && tenantDiscoveryOptions && (
                   <div className="space-y-3 p-4 border rounded-md bg-muted/50">
                     <h4 className="font-medium">{t('auth:availableTenants')}</h4>
                     {tenantDiscoveryOptions.tenants?.map((tenant: any) => (
                       <div key={tenant.id} className="flex items-center justify-between p-2 border rounded">
                         <span>{tenant.business_name}</span>
                         <Button
                           size="sm"
                           onClick={() => {
                             // Set tenant context and proceed with login
                             const mockEvent = { preventDefault: () => {} } as React.FormEvent;
                             handleLogin(mockEvent);
                           }}
                         >
                           {t('auth:join')}
                         </Button>
                       </div>
                     ))}
                   </div>
                 )} */}

                 <div className="space-y-2">
                   <Button type="submit" className="w-full" disabled={isLoading}>
                     {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                     {t('auth:signInButton')}
                   </Button>

                   {/* Find My Accounts Button - Hidden for now, can be re-enabled later */}
                   {/* {!showTenantDiscovery && (
                     <Button
                       type="button"
                       variant="outline"
                       className="w-full"
                       onClick={handleTenantDiscovery}
                       disabled={isLoading || !loginData.email}
                     >
                       {t('auth:findMyTenants')}
                     </Button>
                   )} */}
                 </div>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              <div className="mb-4 text-center">
                <h3 className="font-semibold text-lg">{t('auth:createAccountTitle')}</h3>
                <p className="text-sm text-muted-foreground">{t('auth:createAccountSubtitle')}</p>
              </div>
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t('auth:email')}</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    placeholder={t('auth:emailPlaceholder')}
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-name">{t('auth:fullName')}</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    autoComplete="name"
                    placeholder={t('auth:fullNamePlaceholder')}
                    value={signupData.fullName}
                    onChange={(e) => setSignupData({ ...signupData, fullName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business-name">{t('auth:businessName')}</Label>
                  <Input
                    id="business-name"
                    type="text"
                    autoComplete="organization"
                    placeholder={t('auth:businessNamePlaceholder')}
                    value={signupData.businessName}
                    onChange={(e) => setSignupData({ ...signupData, businessName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone-number">{t('auth:businessPhone')}</Label>
                  <Input
                    id="phone-number"
                    type="tel"
                    autoComplete="tel"
                    placeholder={t('auth:businessPhonePlaceholder')}
                    value={signupData.phoneNumber}
                    onChange={(e) => setSignupData({ ...signupData, phoneNumber: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t('auth:password')}</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showSignupPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder={t('auth:passwordPlaceholder')}
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                      aria-label={showSignupPassword ? t('auth:hidePassword') : t('auth:showPassword')}
                    >
                      {showSignupPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">{t('auth:confirmPassword')}</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showSignupConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder={t('auth:confirmPasswordPlaceholder')}
                      value={signupData.confirmPassword}
                      onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                      aria-label={showSignupConfirmPassword ? t('auth:hidePassword') : t('auth:showPassword')}
                    >
                      {showSignupConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth:signUpButton')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      )}
      </div>
    </div>
  );
}